import { Box, GlobalStyles } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getWebSocketUrl } from "src/config";
import {
  CompiledLayoutRenderer,
  LayoutDataProvider,
  RuntimeLayoutFullscreenShell,
} from "src/shared/ui/src/display-editor";
import { AVAILABLE_WIDGETS } from "src/shared/ui/src/widgets/WidgetConfig";
import { DEFAULTS } from "src/shared/util/src";
import icon from "../../assets/icon.png";
import { useWebControlRoomSocket } from "src/hooks/useWebControlRoomSocket";
import { useResolvedRuntimeLayout } from "src/hooks/useResolvedRuntimeLayout";
import {
  isWebWidgetRouteId,
  widgetRouteIdToTargetKey,
} from "src/hooks/runtimeLayoutTargets";
import { useWebLayoutData } from "src/hooks/useWebLayoutData";
import { useControlRoomTarget } from "src/shared/ui/src/display-editor/useControlRoomTarget";
import type { RuntimeControlRoomDispatch } from "src/shared/util/src/layout-compiler/types";
import { useUserStore } from "src/stores/userStore";

const runtimeGlobalStyles = (
  <GlobalStyles
    styles={{
      ":root": {
        backgroundColor: "rgba(255, 255, 255, 0)",
      },
      "*": {
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      },
      "html, body": {
        margin: 0,
        padding: 0,
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "transparent",
      },
      "#root": {
        height: "100vh",
        overflow: "hidden",
        margin: 0,
        padding: 0,
      },
    }}
  />
);

/**
 * Renders a runtime widget in a standalone fullscreen page without chrome.
 */
export const WidgetView = () => {
  const { id, widgetId, customWidgetId } = useParams<{
    id: string;
    widgetId: string;
    customWidgetId: string;
  }>();
  const widget = AVAILABLE_WIDGETS.find((item) => item.id === widgetId);
  const { token } = useUserStore();
  const widgetTargetKey = customWidgetId
    ? `custom:${customWidgetId}`
    : widgetId && isWebWidgetRouteId(widgetId)
      ? widgetRouteIdToTargetKey(widgetId)
      : null;
  const { meet, themeColors, layoutData, currentLayoutDisciplineKey } =
    useWebLayoutData({
      meetId: id ?? "",
      serverUrl: getWebSocketUrl(),
      token: token ?? undefined,
      enabled: Boolean(id),
    });
  const {
    customLayout,
    isLoading: isLayoutLoading,
    loadLayoutFontFaces,
    targetLabel: resolvedTargetLabel,
  } = useResolvedRuntimeLayout({
    meetId: id,
    token,
    targetKey: widgetTargetKey,
    disciplineKey: currentLayoutDisciplineKey,
    layoutType: "widget",
    websocketUrl: getWebSocketUrl(),
  });

  const layoutCatalogReady = !isLayoutLoading;
  const useCustomLayout = Boolean(customLayout);

  const colors = themeColors ?? DEFAULTS;
  const canPublishControlRoomTarget = Boolean(
    customLayout && meet?.id && id && widgetTargetKey,
  );
  const controlRoomSocket = useWebControlRoomSocket({
    websocketUrl: getWebSocketUrl(),
    enabled: canPublishControlRoomTarget,
  });
  const controlRoomDispatchIdRef = useRef(0);
  const [controlRoomDispatches, setControlRoomDispatches] = useState<
    RuntimeControlRoomDispatch[]
  >([]);
  const handleControlRoomSignal = useCallback((combos: string[]) => {
    setControlRoomDispatches(
      combos.map((combo) => ({
        id: ++controlRoomDispatchIdRef.current,
        combo,
      })),
    );
  }, []);

  const WidgetComponent = widget?.component;

  useControlRoomTarget({
    socket: controlRoomSocket as never,
    document: customLayout,
    meetId: id,
    targetLabel:
      widget?.name ??
      resolvedTargetLabel ??
      customWidgetId ??
      widgetId ??
      "widget",
    enabled: canPublishControlRoomTarget,
    onSignal: handleControlRoomSignal,
  });

  const isLayoutRuntimeReady = Boolean(customLayout && meet?.id);
  const useLayoutCompilerRuntime = Boolean(widgetTargetKey);

  if (!id || (!widgetId && !customWidgetId)) {
    return runtimeGlobalStyles;
  }

  if (useLayoutCompilerRuntime && useCustomLayout) {
    const canRenderLayout = Boolean(
      layoutCatalogReady && customLayout && isLayoutRuntimeReady,
    );

    return (
      <>
        {runtimeGlobalStyles}
        <RuntimeLayoutFullscreenShell>
          {canRenderLayout && customLayout ? (
            <LayoutDataProvider
              data={layoutData}
              getLayoutFontFaces={loadLayoutFontFaces}
            >
              <CompiledLayoutRenderer
                document={customLayout}
                data={layoutData}
                scale={1}
                controlRoomDispatches={controlRoomDispatches}
              />
            </LayoutDataProvider>
          ) : null}
        </RuntimeLayoutFullscreenShell>
      </>
    );
  }

  if (customWidgetId) {
    return runtimeGlobalStyles;
  }

  if (!widget || !meet || !WidgetComponent) {
    return runtimeGlobalStyles;
  }

  return (
    <>
      {runtimeGlobalStyles}
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          position: "fixed",
          inset: 0,
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        <WidgetComponent
          colors={colors}
          widget
          icon={icon}
          records={meet.records}
          serverUrl={getWebSocketUrl()}
          token={token ?? undefined}
          meetId={id}
        />
      </Box>
    </>
  );
};
